/* Modèle de navigation et adaptateur react-router — Factumis app.

   Le prototype naviguait via go(routeId, ctx) avec une chaîne `route`. On conserve
   cette API ergonomique au-dessus de react-router : les écrans portent presque
   tels quels, les URLs deviennent réelles (/factures/:id, etc.).

   Contenu :
   - NAV      : arbre des groupes/items affichés dans la Sidebar.
   - PATHS    : routeId → chemin URL de base.
   - TITLES   : routeId → [titre de page, libellé fil d'Ariane|null].
   - PARENT   : routes enfants → id parent (pour le surlignage sidebar).
   - useGo()  : hook de navigation programmatique (go(routeId, ctx?)).
   - useCtx() : fusionne location.state + :id URL param en objet ctx.
   - activeNavId() : déduit l'id sidebar actif depuis le pathname. */
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { IconName } from '../lib/icons';

/** Aucun écran connu derrière ce chemin : la Sidebar ne surligne rien, la Topbar
    et document.title affichent « Page introuvable » au lieu de « Tableau de bord ». */
export const NOTFOUND_NAV_ID = 'notfound';

export type NavItem = { id: string; label: string; icon: IconName; badge?: number };
export type NavGroup = { grp: string; items: NavItem[] };

export const NAV: NavGroup[] = [
  {
    grp: 'Pilotage',
    items: [
      { id: 'dashboard', label: 'Tableau de bord', icon: 'grid' },
      { id: 'micro', label: 'Cotisations', icon: 'receipt' },
    ],
  },
  {
    grp: 'Facturation',
    items: [
      { id: 'invoices', label: 'Factures émises', icon: 'file-out' },
      { id: 'received', label: 'Factures reçues', icon: 'file-in' },
      { id: 'recurring', label: 'Récurrentes', icon: 'clock' },
      { id: 'quotes', label: 'Devis', icon: 'quote' },
      { id: 'catalog', label: 'Catalogue', icon: 'tag' },
      { id: 'templates', label: 'Modèles', icon: 'copy' },
    ],
  },
  {
    grp: 'Carnet',
    items: [
      { id: 'clients', label: 'Clients', icon: 'users' },
      { id: 'suppliers', label: 'Fournisseurs', icon: 'truck' },
    ],
  },
  {
    grp: 'Conformité',
    items: [
      { id: 'ereporting', label: 'E-reporting', icon: 'report' },
      { id: 'conformity', label: 'Conformité', icon: 'shield' },
    ],
  },
  {
    grp: 'Compte',
    items: [
      { id: 'settings', label: 'Paramètres', icon: 'gear' },
      { id: 'team', label: 'Équipe', icon: 'team' },
      { id: 'subscription', label: 'Abonnement', icon: 'card' },
      { id: 'support', label: 'Support', icon: 'help' },
      { id: 'confidentialite', label: 'Confidentialité', icon: 'shield' },
    ],
  },
];

// child route → parent nav id (for sidebar highlight)
export const PARENT: Record<string, string> = {
  invoice: 'invoices',
  create: 'invoices',
  client: 'clients',
  'client-new': 'clients',
  'client-edit': 'clients',
  quote: 'quotes',
  supplier: 'suppliers',
  'received-detail': 'received',
};

// [title, breadcrumb-parent]
export const TITLES: Record<string, [string, string | null]> = {
  dashboard: ['Tableau de bord', null],
  micro: ['Cotisations', null],
  invoices: ['Factures émises', null],
  invoice: ['Détail de facture', 'Factures émises'],
  create: ['Nouvelle facture', 'Factures émises'],
  received: ['Factures reçues', null],
  'received-detail': ['Facture reçue', 'Factures reçues'],
  recurring: ['Factures récurrentes', null],
  quotes: ['Devis', null],
  quote: ['Détail du devis', 'Devis'],
  catalog: ['Catalogue', null],
  templates: ['Modèles', null],
  clients: ['Clients', null],
  client: ['Fiche client', 'Clients'],
  'client-new': ['Nouveau client', 'Clients'],
  'client-edit': ['Modifier le client', 'Clients'],
  suppliers: ['Fournisseurs', null],
  supplier: ['Fiche fournisseur', 'Fournisseurs'],
  ereporting: ['E-reporting', null],
  conformity: ['Conformité', null],
  settings: ['Paramètres', null],
  team: ['Équipe', null],
  subscription: ['Abonnement', null],
  support: ['Support', null],
  confidentialite: ['Politique de confidentialité', null],
  notifications: ['Notifications', null],
  // Hors menu NAV : atteint depuis Paramètres ou l'onboarding.
  mandate: ['Mandat de représentation', null],
  admin: ['Admin · Waitlist', null],
  [NOTFOUND_NAV_ID]: ['Page introuvable', null],
};

// routeId → URL base
export const PATHS: Record<string, string> = {
  dashboard: '/dashboard',
  micro: '/cotisations',
  invoices: '/factures',
  invoice: '/factures',
  create: '/factures/nouvelle',
  received: '/recues',
  'received-detail': '/recues',
  recurring: '/factures-recurrentes',
  quotes: '/devis',
  quote: '/devis',
  catalog: '/catalogue',
  templates: '/modeles',
  clients: '/clients',
  client: '/clients',
  'client-new': '/clients/nouveau',
  'client-edit': '/clients/modifier',
  suppliers: '/fournisseurs',
  supplier: '/fournisseurs',
  ereporting: '/e-reporting',
  conformity: '/conformite',
  settings: '/parametres',
  team: '/equipe',
  subscription: '/abonnement',
  support: '/support',
  confidentialite: '/confidentialite',
  notifications: '/notifications',
  mandate: '/mandat',
  admin: '/admin',
};

const DETAIL = new Set(['invoice', 'received-detail', 'quote', 'client', 'client-edit', 'supplier']);

export type Ctx = Record<string, unknown> | null;

function buildPath(routeId: string, ctx?: Ctx): string {
  const base = PATHS[routeId] || '/dashboard';
  if (DETAIL.has(routeId)) {
    const c = (ctx || {}) as Record<string, unknown>;
    const id = c.id ?? c.name ?? c.siren;
    return id != null ? base + '/' + encodeURIComponent(String(id)) : base;
  }
  return base;
}

/** go(routeId, ctx) — navigate the proto way, on top of react-router. */
export function useGo() {
  const navigate = useNavigate();
  return useCallback(
    (routeId: string, ctx?: Ctx) => {
      navigate(buildPath(routeId, ctx), { state: ctx ?? null });
      window.scrollTo(0, 0);
    },
    [navigate],
  );
}

/** ctx merged from location state + route param (`:id`). */
export function useCtx(): Record<string, unknown> {
  const { state } = useLocation();
  const params = useParams();
  return { ...(params.id ? { id: decodeURIComponent(params.id) } : {}), ...((state as object) || {}) };
}

/** Active sidebar id from the current pathname. */
export function activeNavId(pathname: string): string {
  // La racine redirige toujours vers /dashboard : sans ce cas, le rendu qui
  // précède la redirection affichait un flash « Page introuvable ».
  if (pathname === '/') return 'dashboard';
  let best = '';
  let bestLen = -1;
  for (const [rid, p] of Object.entries(PATHS)) {
    if (pathname === p || pathname.startsWith(p + '/')) {
      if (p.length > bestLen) {
        best = rid;
        bestLen = p.length;
      }
    }
  }
  if (bestLen < 0) return NOTFOUND_NAV_ID;
  return PARENT[best] || best;
}
